interface PaginationProps {
  page: number; // 0-indexed
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** "page" (default): sticks to the bottom of the viewport as a full-bleed bar.
   *  "panel": plain inline bar for use inside modals/cards that already pin their own footer. */
  variant?: "page" | "panel";
}

export default function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  variant = "page",
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);

  const pageNumbers = (() => {
    const windowSize = 5;
    let start = Math.max(0, page - Math.floor(windowSize / 2));
    const end = Math.min(pageCount, start + windowSize);
    start = Math.max(0, end - windowSize);
    return Array.from({ length: end - start }, (_, i) => start + i);
  })();

  const containerClass =
    variant === "page"
      ? "sticky bottom-0 z-20 -mx-6 lg:-mx-8 mt-6 px-6 lg:px-8 py-3 bg-gray-950/95 backdrop-blur-sm border-t border-gray-800 flex items-center justify-between gap-3 flex-wrap"
      : "flex items-center justify-between gap-3 flex-wrap px-5 py-3";

  return (
    <div className={containerClass}>
      <p className="text-xs text-gray-500">
        Showing {from}–{to} of {total}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">Per page</label>
            <input
              type="number"
              min={1}
              value={pageSize}
              onChange={(e) => {
                const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                onPageSizeChange(n);
              }}
              className="w-16 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            />
          </div>
        )}
        <div className="flex gap-1">
          <button
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors disabled:opacity-40"
          >
            Prev
          </button>
          {pageNumbers[0] > 0 && <span className="px-1.5 py-1.5 text-sm text-gray-600">…</span>}
          {pageNumbers.map((p) => (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-[2.25rem] px-2 py-1.5 rounded-lg text-sm transition-colors ${
                p === page ? "bg-indigo-600 text-white" : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
            >
              {p + 1}
            </button>
          ))}
          {pageNumbers[pageNumbers.length - 1] < pageCount - 1 && <span className="px-1.5 py-1.5 text-sm text-gray-600">…</span>}
          <button
            disabled={page >= pageCount - 1}
            onClick={() => onPageChange(page + 1)}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
